const getGlobal = () => {
    let _global;
    if (typeof window !== 'undefined') {
        _global = window;
    }
    else if (typeof global !== 'undefined') {
        _global = global;
    }
    else if (typeof self !== 'undefined') {
        _global = self;
    }
    else {
        _global = {};
    }
    return _global;
};
const _global = getGlobal();

function getEntryFromRegistry(getGlobal, fetchWithJsonp, satisfiesVersion, isSatisfied, { dependency, dependencyVersion, name, version, defaultEntry, identifier, identifierContainer, autoFetch: _autoFetch, autoStore = true, }) {
    const _global = getGlobal();
    let getCacheEntryPromise;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { prefix, registry, registryType, version: mainVersion, main, modules, storage, _onUpdateStorage, _updateStorage, _toBeResolvedUpdateStorage, registryAutoFetch, } = _global[identifierContainer];
    const autoFetch = _autoFetch !== null && _autoFetch !== void 0 ? _autoFetch : registryAutoFetch;
    const storageKey = [identifier, main, mainVersion, prefix, dependency].join(':');
    let fetchPromise;
    if (registry === '*')
        return Promise.resolve({ entry: defaultEntry, fetchPromise });
    // '*' means always use remote entry
    // or the remote version satisfies the dependency version
    // `forcedVersion: true` means always use remote entry even if the remote version is not satisfied
    if (autoFetch) {
        let fetchRawPromise;
        const query = {
            name,
            dependency,
            main,
            mainVersion,
            _: Date.now().toString(),
        };
        if (version) {
            query.version = version;
        }
        const url = `${registry}?${new URLSearchParams(query)}`;
        if (registryType === 'fetch') {
            fetchRawPromise = fetch(url)
                .catch((error) => {
                console.error(`[MFE] Failed to fetch remote entry: ${url}`);
                console.error(error);
            })
                .then((response) => response.json());
        }
        else if (registryType === 'jsonp') {
            fetchRawPromise = fetchWithJsonp(getGlobal, url, name);
        }
        else {
            throw new Error(`[MFE] Invalid registry type: ${registryType}`);
        }
        fetchPromise = fetchRawPromise.then((data) => {
            const remoteData = data === null || data === void 0 ? void 0 : data[dependency];
            if (remoteData &&
                isSatisfied(satisfiesVersion, remoteData, dependencyVersion)) {
                const { entry } = remoteData !== null && remoteData !== void 0 ? remoteData : {};
                if (typeof entry !== 'string') {
                    throw new Error(`[MFE] Invalid entry for ${dependency}: ${entry} from ${url}`);
                }
                if (prefix === '*') {
                    console.warn(`[MFE] The entry of ${dependency} is being cached, but prefix is not validly set, pls set 'prefix' in 'site.config'.`);
                }
                if (!autoStore)
                    return remoteData;
                const result = storage.setItem(storageKey, JSON.stringify(data[dependency]));
                const onUpdate = (_remoteData) => {
                    getCacheEntryPromise.then((options = {}) => {
                        if (options.entry === _remoteData.entry &&
                            options.version === _remoteData.version &&
                            !options.forcedVersion === !_remoteData.forcedVersion)
                            return;
                        _updateStorage.add([dependency, _remoteData, options]);
                        _onUpdateStorage.forEach((callback) => {
                            callback(dependency, _remoteData, options);
                        });
                    });
                };
                if (result instanceof Promise) {
                    result.then(() => {
                        if (_toBeResolvedUpdateStorage) {
                            _toBeResolvedUpdateStorage.add(() => onUpdate(remoteData));
                            return;
                        }
                        onUpdate(remoteData);
                    });
                }
                else {
                    if (_toBeResolvedUpdateStorage) {
                        _toBeResolvedUpdateStorage.add(() => onUpdate(remoteData));
                        return;
                    }
                    onUpdate(remoteData);
                }
            }
        });
    }
    const getEntry = (value) => {
        if (!value)
            return;
        const { entry, forcedVersion, version: cacheVersion = '*', meta, } = JSON.parse(value);
        return {
            meta,
            entry,
            forcedVersion,
            version: cacheVersion,
        };
    };
    const value = storage.getItem(storageKey);
    if (value instanceof Promise) {
        getCacheEntryPromise = value.then(getEntry);
    }
    else {
        getCacheEntryPromise = Promise.resolve(value).then(getEntry);
    }
    return getCacheEntryPromise.then((options) => {
        let currentEntry = defaultEntry;
        if (isSatisfied(satisfiesVersion, options, dependencyVersion)) {
            currentEntry = options.entry;
            const currentVersion = options.version;
            Object.values(modules).forEach((module) => {
                var _a;
                if ((_a = module.dependencies) === null || _a === void 0 ? void 0 : _a[dependency]) {
                    module.dependencies[dependency].entry = currentEntry;
                    module.dependencies[dependency].version = currentVersion;
                    module.dependencies[dependency].forcedVersion =
                        !!options.forcedVersion;
                    module.dependencies[dependency].meta = options.meta;
                }
            });
        }
        return { entry: currentEntry, fetchPromise };
    });
}

const injectScript = ({ url, retryTimes, retryDelay, injectScript: _injectScript, }) => {
    return new Promise((resolve, reject) => {
        const element = document.createElement('script');
        element.src = url;
        element.async = true;
        element.type = 'text/javascript';
        element.onload = () => {
            resolve();
            document.head.removeChild(element);
        };
        element.onerror = () => {
            document.head.removeChild(element);
            if (retryTimes > 0) {
                setTimeout(() => {
                    _injectScript({
                        url,
                        retryTimes: retryTimes - 1,
                        retryDelay,
                        injectScript: _injectScript,
                    }).then(resolve, reject);
                }, retryDelay);
                return;
            }
            reject(new Error(`[MFE] Script Error: ${url}`));
        };
        document.head.appendChild(element);
    });
};

/* eslint-disable no-shadow */
const identifier = 'rc-mfe';
const customElementName = 'rc-mfe';
const identifierContainer = `__RC_MFE__`;
const identifierAttribute = `data-${identifier}`;

const fetchWithJsonp = (getGlobal, url, _name) => {
    var _a, _b;
    const _global = getGlobal();
    const jsonpKey = '__RC_MFE_REGISTRY_CALLBACK__';
    const jsonpPromisesKey = '__RC_MFE_REGISTRY_PROMISES__';
    _global[jsonpKey] = (_a = _global[jsonpKey]) !== null && _a !== void 0 ? _a : {};
    _global[jsonpPromisesKey] = (_b = _global[jsonpPromisesKey]) !== null && _b !== void 0 ? _b : {};
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (_global[jsonpPromisesKey][_name])
        return _global[jsonpPromisesKey][_name];
    _global[jsonpPromisesKey][_name] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const callback = (_global[jsonpKey][_name] = (data) => {
            document.body.removeChild(script);
            if (_global[jsonpKey][_name] === callback) {
                _global[jsonpKey][_name] = null;
            }
            delete _global[jsonpPromisesKey][_name];
            resolve(data);
        });
        script.src = url;
        script.onerror = () => {
            document.body.removeChild(script);
            if (_global[jsonpKey][_name] === callback) {
                _global[jsonpKey][_name] = null;
            }
            delete _global[jsonpPromisesKey][_name];
            reject(new Error(`[MFE] '${url}' jsonp fetch failed.`));
        };
        document.body.appendChild(script);
    });
    return _global[jsonpPromisesKey][_name];
};

/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-globals */
/* eslint-disable no-else-return */
/* eslint-disable no-use-before-define */
// source code from `https://github.com/omichelsen/compare-versions`
// !!! DO NOT MODIFY THIS FILE !!!
// !!! Ensure that `satisfiesVersion` is serializable.
function satisfiesVersion(version, range) {
    /**
     * Compare [semver](https://semver.org/) version strings to find greater, equal or lesser.
     * This library supports the full semver specification, including comparing versions with different number of digits like `1.0.0`, `1.0`, `1`, and pre-release versions like `1.0.0-alpha`.
     * @param v1 - First version to compare
     * @param v2 - Second version to compare
     * @returns Numeric value compatible with the [Array.sort(fn) interface](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#Parameters).
     */
    const compareVersions = (v1, v2) => {
        // validate input and split into segments
        const n1 = validateAndParse(v1);
        const n2 = validateAndParse(v2);
        // pop off the patch
        const p1 = n1.pop();
        const p2 = n2.pop();
        // validate numbers
        const r = compareSegments(n1, n2);
        if (r !== 0)
            return r;
        // validate pre-release
        if (p1 && p2) {
            return compareSegments(p1.split('.'), p2.split('.'));
        }
        else if (p1 || p2) {
            return p1 ? -1 : 1;
        }
        return 0;
    };
    /**
     * Compare [semver](https://semver.org/) version strings using the specified operator.
     *
     * @param v1 First version to compare
     * @param v2 Second version to compare
     * @param operator Allowed arithmetic operator to use
     * @returns `true` if the comparison between the firstVersion and the secondVersion satisfies the operator, `false` otherwise.
     *
     * @example
     * ```
     * compare('10.1.8', '10.0.4', '>'); // return true
     * compare('10.0.1', '10.0.1', '='); // return true
     * compare('10.1.1', '10.2.2', '<'); // return true
     * compare('10.1.1', '10.2.2', '<='); // return true
     * compare('10.1.1', '10.2.2', '>='); // return false
     * ```
     */
    const compare = (v1, v2, operator) => {
        // validate input operator
        assertValidOperator(operator);
        // since result of compareVersions can only be -1 or 0 or 1
        // a simple map can be used to replace switch
        const res = compareVersions(v1, v2);
        return operatorResMap[operator].includes(res);
    };
    /**
     * Match [npm semver](https://docs.npmjs.com/cli/v6/using-npm/semver) version range.
     *
     * @param version Version number to match
     * @param range Range pattern for version
     * @returns `true` if the version number is within the range, `false` otherwise.
     *
     * @example
     * ```
     * satisfies('1.1.0', '^1.0.0'); // return true
     * satisfies('1.1.0', '~1.0.0'); // return false
     * ```
     */
    const satisfies = (version, range) => {
        // handle multiple comparators
        if (range.includes('||')) {
            return range.split('||').some((r) => satisfies(version, r));
        }
        else if (range.includes(' ')) {
            return range
                .trim()
                .replace(/\s{2,}/g, ' ')
                .split(' ')
                .every((r) => satisfies(version, r));
        }
        // if no range operator then "="
        const m = range.match(/^([<>=~^]+)/);
        const op = m ? m[1] : '=';
        // if gt/lt/eq then operator compare
        if (op !== '^' && op !== '~')
            return compare(version, range, op);
        // else range of either "~" or "^" is assumed
        const [v1, v2, v3, , vp] = validateAndParse(version);
        const [r1, r2, r3, , rp] = validateAndParse(range);
        const v = [v1, v2, v3];
        const r = [r1, r2 !== null && r2 !== void 0 ? r2 : 'x', r3 !== null && r3 !== void 0 ? r3 : 'x'];
        // validate pre-release
        if (rp) {
            if (!vp)
                return false;
            if (compareSegments(v, r) !== 0)
                return false;
            if (compareSegments(vp.split('.'), rp.split('.')) === -1)
                return false;
        }
        // first non-zero number
        const nonZero = r.findIndex((v) => v !== '0') + 1;
        // pointer to where segments can be >=
        const i = op === '~' ? 2 : nonZero > 1 ? nonZero : 1;
        // before pointer must be equal
        if (compareSegments(v.slice(0, i), r.slice(0, i)) !== 0)
            return false;
        // after pointer must be >=
        if (compareSegments(v.slice(i), r.slice(i)) === -1)
            return false;
        return true;
    };
    const semver = /^[v^~<>=]*?(\d+)(?:\.([x*]|\d+)(?:\.([x*]|\d+)(?:\.([x*]|\d+))?(?:-([\da-z\-]+(?:\.[\da-z\-]+)*))?(?:\+[\da-z\-]+(?:\.[\da-z\-]+)*)?)?)?$/i;
    const validateAndParse = (version) => {
        if (typeof version !== 'string') {
            throw new TypeError('Invalid argument expected string');
        }
        const match = version.match(semver);
        if (!match) {
            throw new Error(`Invalid argument not valid semver ('${version}' received)`);
        }
        match.shift();
        return match;
    };
    const isWildcard = (s) => s === '*' || s === 'x' || s === 'X';
    const tryParse = (v) => {
        const n = parseInt(v, 10);
        return isNaN(n) ? v : n;
    };
    const forceType = (a, b) => typeof a !== typeof b ? [String(a), String(b)] : [a, b];
    const compareStrings = (a, b) => {
        if (isWildcard(a) || isWildcard(b))
            return 0;
        const [ap, bp] = forceType(tryParse(a), tryParse(b));
        if (ap > bp)
            return 1;
        if (ap < bp)
            return -1;
        return 0;
    };
    const compareSegments = (a, b) => {
        for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
            const r = compareStrings(a[i] || '0', b[i] || '0');
            if (r !== 0)
                return r;
        }
        return 0;
    };
    const operatorResMap = {
        '>': [1],
        '>=': [0, 1],
        '=': [0],
        '<=': [-1, 0],
        '<': [-1],
    };
    const allowedOperators = Object.keys(operatorResMap);
    const assertValidOperator = (op) => {
        if (typeof op !== 'string') {
            throw new TypeError(`Invalid operator type, expected string but got ${typeof op}`);
        }
        if (allowedOperators.indexOf(op) === -1) {
            throw new Error(`Invalid operator, expected one of ${allowedOperators.join('|')}`);
        }
    };
    return satisfies(version, range);
}

/* eslint-disable no-unused-expressions */
/* eslint-disable lines-between-class-members */
/*
 * @Author: Paynter Chen
 * @Date: 2022-09-23 19:27:37
 * @Last Modified by: mikey.zhaopeng
 * @Last Modified time: 2023-04-04 23:59:56
 */
const logOption = {
    enable: false,
};
class ConsoleLoggerCore {
    log(...params) {
        console.log(...params);
    }
    debug(...params) {
        console.debug(...params);
    }
    info(...params) {
        console.info(...params);
    }
    warn(...params) {
        console.warn(...params);
    }
    error(...params) {
        console.error(...params);
    }
}
const logCoreManager = (() => {
    const consoleLogCore = new ConsoleLoggerCore();
    let customLogCore;
    return {
        get() {
            return customLogCore || consoleLogCore;
        },
        setCustomLogCore(core) {
            customLogCore = core;
        },
        enable(enable = true) {
            logOption.enable = enable;
        },
    };
})();
class Logger {
    constructor(_tags = []) {
        this._tags = _tags;
        this._prefix = [];
        this._tagsString = _tags.join(',');
    }
    prefix(prefix) {
        this._prefix = prefix;
        return this;
    }
    tags(...tags) {
        return new Logger([...this._tags, ...tags]);
    }
    log(...params) {
        logOption.enable &&
            logCoreManager.get().log(...this._prefix, this._tagsString, ...params);
    }
    debug(...params) {
        logOption.enable &&
            logCoreManager.get().debug(...this._prefix, this._tagsString, ...params);
    }
    info(...params) {
        logOption.enable &&
            logCoreManager.get().info(...this._prefix, this._tagsString, ...params);
    }
    warn(...params) {
        logOption.enable &&
            logCoreManager.get().warn(...this._prefix, this._tagsString, ...params);
    }
    error(...params) {
        logOption.enable &&
            logCoreManager.get().error(...this._prefix, this._tagsString, ...params);
    }
}
const getStyles = (backgroundColor) => {
    return [
        `background: ${backgroundColor}`,
        `border-radius: 0.5em`,
        `color: white`,
        `font-weight: bold`,
        `padding: 2px 0.5em`,
    ].join(';');
};
const commonPrefixStyles = getStyles(`#7f8c8d`);
const getLoggerPrefix = (prefixText, color) => [
    `%c${prefixText}`,
    color ? getStyles(color) : commonPrefixStyles,
];
const logger = new Logger(['@rc-mfe']);

const isSatisfied = (satisfiesVersion, options, dependencyVersion) => {
    let matchResult = false;
    try {
        matchResult = options
            ? satisfiesVersion(options.version, dependencyVersion)
            : matchResult;
    }
    catch (e) {
        //
    }
    return !!((options === null || options === void 0 ? void 0 : options.entry) &&
        (dependencyVersion === '*' || matchResult || options.forcedVersion));
};

export { customElementName, fetchWithJsonp, getEntryFromRegistry, getGlobal, getLoggerPrefix, _global as global, identifier, identifierAttribute, identifierContainer, injectScript, isSatisfied, logCoreManager, logger, satisfiesVersion };
//# sourceMappingURL=index.mjs.map
